import { Box, ButtonBase } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, MOTION, NEUTRAL } from "../../../theme/tokens";
// Option ids can be arbitrary domain values ("Registered Nurse (RN)") — slugged for stable kebab-case test ids.
import { toTestIdSlug } from "../../../utils/strings";
import { SystemRoundedCheckbox } from "../Clickable/SystemRoundedCheckbox";
import { filterSearchFieldProps } from "../Inputs/filterSearchFieldProps";
import { SystemValidationTextField } from "../Inputs/SystemValidationTextField";
import { FilterSectionHeader } from "./FilterSectionHeader";

export type FacetFilterOption = {
	/** Stable identity for the option (used for toggling + test ids). */
	id: string;
	/** Already-translated display label. */
	label: string;
	/** Live facet count. Omit to hide the count column for this option. */
	count?: number;
	checked: boolean;
	/**
	 * Explicit disable/enable. When omitted, unchecked options with `count === 0` are disabled
	 * automatically; pass `false` to opt out (e.g. a view toggle that reveals extra rows and
	 * must stay interactive at count 0).
	 */
	disabled?: boolean;
	/** Categorical color dot rendered before the label (role/job colors). */
	colorDot?: string;
};

type FacetFilterSectionProps = {
	/** Already-translated group title. */
	title: string;
	options: FacetFilterOption[];
	onToggle: (id: string, checked: boolean) => void;
	/** Whether the group starts expanded. Default true. */
	defaultExpanded?: boolean;
	/**
	 * Max options rendered before the "Show all (k)" expander. Options are ranked by
	 * count (desc) for the visible subset; checked options are always visible.
	 */
	visibleLimit?: number;
	dataTestId?: string;
	/** Notified when the group header expand/collapse is toggled (analytics, and controlled-mode updates). */
	onExpandedChange?: (expanded: boolean) => void;
	/** Notified when the "Show all"/"Show less" expander is toggled (analytics). */
	onShowAllChange?: (showAll: boolean) => void;
	/**
	 * Controlled expansion. Omit to keep the group's own internal state (which resets on remount);
	 * pass it to persist collapse across visits — whoever never filters by Union shouldn't have to
	 * close it again every time.
	 *
	 * All-or-nothing, and it must be STANDING: a caller that passes a boolean sometimes and
	 * `undefined` the rest of the time flips the group between controlled and uncontrolled, and the
	 * value it forced is discarded the instant it stops passing one. To open a group from elsewhere
	 * in the page, use `highlighted` — that commits.
	 */
	expanded?: boolean;
	/**
	 * How an unchecked option's count reads when the group already has a selection.
	 *
	 * `absolute` (default) shows the raw count. That is correct-by-construction — each count is the
	 * rows that option would produce — but it does not ADD UP: with "Active" pre-checked, the
	 * Inactive count sits outside the visible result, so the numbers in one group can sum to more
	 * than the table shows and the whole rail stops being trusted.
	 *
	 * `additive` renders those as `+N` instead. Same number, honest arithmetic: it reads as "checking
	 * this adds N rows", which is exactly what it does, since options within a group are OR'ed.
	 */
	countMode?: "absolute" | "additive";
	/**
	 * Collapse unchecked zero-count options behind one "N with no matches" line. Off by default,
	 * which leaves them visible-but-disabled. Turn it on for long groups, where a run of dead
	 * options is what the eye hits before it reaches anything useful.
	 */
	foldZeroCounts?: boolean;
	/** Add a filter-the-options search box. For groups with enough values that scanning them is the work. */
	searchable?: boolean;
	/**
	 * Scroll this group into view, open it, and flash it.
	 *
	 * For handing a user off from somewhere else in the page — a column's "Filter by…" — where landing
	 * them in a long rail with no indication of WHICH group they were sent to would leave them
	 * hunting. The flash is what makes the jump legible; without it the panel just appears to have
	 * scrolled on its own.
	 *
	 * Opening is part of the hand-off, not the caller's job: a jump to a collapsed group is a jump to
	 * nothing. The group STAYS open afterwards — releasing this flag ends the flash, never the
	 * expansion. A caller driving `expanded` still wins, since that prop is a standing declaration.
	 */
	highlighted?: boolean;
	/** Placeholder for the `searchable` box. Defaults to a generic "Search". */
	searchPlaceholder?: string;
};

const DEFAULT_VISIBLE_LIMIT = 6;
/**
 * How long the jumped-to flash runs. Re-exported from the motion tokens because the page that
 * triggers the jump has to release its "focused group" state on the same clock — if the two drift,
 * the wash either cuts off mid-animation or lingers after it.
 */
export const FACET_SECTION_FLASH_MS = MOTION.DURATION.FACET_FLASH;
const COLOR_DOT_SIZE = 8;
/** Above this many options a group gets its own search box when `searchable` is on. */
const SEARCHABLE_MIN_OPTIONS = 8;

/**
 * A titled, collapsible group of checkbox filter options with live facet counts —
 * the building block of a Booking.com-style "Filters" sidebar. Compose inside
 * `FilterSidebar`. Zero-count unchecked options stay visible but disabled so the
 * user sees the full option space without dead-end clicks.
 */
export const FacetFilterSection: FC<FacetFilterSectionProps> = ({
	title,
	options,
	onToggle,
	// DEFAULT: groups start open — the sidebar's value is in seeing the options, so expanded is the useful default.
	defaultExpanded = true,
	// DEFAULT: show the top few options before a "Show all" expander — a sane cap for a compact sidebar column.
	visibleLimit = DEFAULT_VISIBLE_LIMIT,
	dataTestId,
	onExpandedChange,
	onShowAllChange,
	expanded: controlledExpanded,
	countMode = "absolute",
	foldZeroCounts = false,
	searchable = false,
	searchPlaceholder,
	highlighted = false,
}) => {
	const { t } = useTranslation();
	const titleId = useId();
	const sectionRef = useRef<HTMLDivElement>(null);
	const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
	const [showAll, setShowAll] = useState(false);
	const [showFolded, setShowFolded] = useState(false);
	const [query, setQuery] = useState("");

	const expanded = controlledExpanded ?? uncontrolledExpanded;
	const searchEnabled = searchable && options.length >= SEARCHABLE_MIN_OPTIONS;
	// A group with a selection is the only case where an unchecked count sits outside the result.
	const showAdditiveCounts = countMode === "additive" && options.some((o) => o.checked);

	const queriedOptions = useMemo(() => {
		const trimmed = query.trim().toLowerCase();
		if (!searchEnabled || !trimmed) return options;
		// Checked options always survive the query, so a search can never hide an active filter.
		return options.filter((o) => o.checked || o.label.toLowerCase().includes(trimmed));
	}, [options, query, searchEnabled]);

	// Unchecked dead ends, pulled out of the main list so a long group leads with what works.
	const foldedOptions = useMemo(() => (foldZeroCounts ? queriedOptions.filter((o) => o.count === 0 && !o.checked) : []), [queriedOptions, foldZeroCounts]);
	const liveOptions = useMemo(
		() => (foldedOptions.length ? queriedOptions.filter((o) => !foldedOptions.includes(o)) : queriedOptions),
		[queriedOptions, foldedOptions],
	);

	const visibleOptions = useMemo(() => {
		if (showAll || liveOptions.length <= visibleLimit) return liveOptions;
		// Rank by count desc to surface the most useful facets, but render in the caller's
		// order so rows don't jump around as counts change. Checked options never hide.
		const ranked = [...liveOptions].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
		const visibleIds = new Set(ranked.slice(0, visibleLimit).map((o) => o.id));
		return liveOptions.filter((o) => o.checked || visibleIds.has(o.id));
	}, [liveOptions, showAll, visibleLimit]);

	const hasExpander = liveOptions.length > visibleLimit;

	const renderCount = (option: FacetFilterOption): ReactNode => {
		if (option.count === undefined) return undefined;
		if (showAdditiveCounts && !option.checked && option.count > 0) return `+${option.count}`;
		return option.count;
	};

	// `block: "nearest"` so a group already on screen doesn't jump — only an off-screen one scrolls.
	//
	// Opening is COMMITTED into the group's own state, not applied as a transient override. A caller
	// that instead forced `expanded` while the jump was in flight would hand the group back to
	// `uncontrolledExpanded` the moment it released — snapping a `defaultExpanded: false` group shut
	// seconds after landing on it, which is the opposite of what a hand-off is for.
	useEffect(() => {
		if (!highlighted) return;
		setUncontrolledExpanded(true);
		sectionRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
	}, [highlighted]);

	const handleExpandToggle = () => {
		const next = !expanded;
		setUncontrolledExpanded(next);
		onExpandedChange?.(next);
	};

	const handleShowAllToggle = () => {
		const next = !showAll;
		setShowAll(next);
		onShowAllChange?.(next);
	};

	return (
		<Box
			ref={sectionRef}
			role="group"
			aria-labelledby={titleId}
			data-testid={dataTestId}
			sx={(theme) => ({
				borderRadius: "6px",
				...(highlighted
					? {
							"@keyframes facetSectionFlash": {
								// Most of the duration is a held wash, not a fade: a long fade reads as a rendering
								// artifact, while a held tint reads as "this one".
								"0%, 65%": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
								"100%": { backgroundColor: "transparent" },
							},
							animation: `facetSectionFlash ${FACET_SECTION_FLASH_MS}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": {
								animation: "none",
								// Reduced motion still needs the answer to "which group?", so hold the wash instead.
								backgroundColor: alpha(theme.palette.primary.main, 0.1),
							},
						}
					: {}),
			})}
		>
			<FilterSectionHeader
				title={title}
				titleId={titleId}
				expanded={expanded}
				onToggle={handleExpandToggle}
				// Checked options are this group's active filters, so the collapsed header still says how
				// many are narrowing the table.
				activeCount={options.reduce((total, option) => total + (option.checked ? 1 : 0), 0)}
				dataTestId={dataTestId ? `${dataTestId}-header` : undefined}
			/>

			{expanded && (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						pb: 0.5,
						// Entrance: opacity only (GPU-cheap); height snaps so collapse feels instant.
						"@keyframes facetSectionFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
						animation: `facetSectionFadeIn ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { animation: "none" },
					}}
				>
					{searchEnabled && (
						<SystemValidationTextField
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							onClear={query ? () => setQuery("") : undefined}
							placeholder={searchPlaceholder ?? t("filter.section.search_placeholder")}
							{...filterSearchFieldProps("facet-option-search")}
							fullWidth
							formControlSx={{ mb: 0.5 }}
							dataTestId={dataTestId ? `${dataTestId}-search` : undefined}
						/>
					)}

					{visibleOptions.map((option) => {
						const disabled = option.disabled ?? (option.count === 0 && !option.checked);
						return (
							<SystemRoundedCheckbox
								key={option.id}
								checked={option.checked}
								onChange={(checked) => onToggle(option.id, checked)}
								label={option.label}
								disabled={disabled}
								fullWidth
								secondary={renderCount(option)}
								startAdornment={
									option.colorDot ? (
										<Box sx={{ width: COLOR_DOT_SIZE, height: COLOR_DOT_SIZE, borderRadius: "50%", flexShrink: 0, bgcolor: option.colorDot }} />
									) : undefined
								}
								data-testid={dataTestId ? `${dataTestId}-option-${toTestIdSlug(option.id)}` : undefined}
								sx={{
									py: 0.375,
									borderRadius: "6px",
									"@media (hover: hover) and (pointer: fine)": {
										"&:hover": { backgroundColor: disabled ? "transparent" : NEUTRAL[50] },
									},
								}}
							/>
						);
					})}

					{hasExpander && (
						<ButtonBase
							onClick={handleShowAllToggle}
							data-testid={dataTestId ? `${dataTestId}-show-all` : undefined}
							sx={(theme) => ({
								alignSelf: "flex-start",
								mt: 0.5,
								px: 0.5,
								py: 0.25,
								borderRadius: "6px",
								fontSize: FONT_SIZE.SECONDARY,
								fontWeight: 600,
								fontFamily: "inherit",
								color: theme.palette.primary.main,
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": { textDecoration: "underline" },
								},
							})}
						>
							{showAll ? t("filter.section.show_less") : t("filter.section.show_all", { count: liveOptions.length })}
						</ButtonBase>
					)}

					{foldedOptions.length > 0 && (
						<>
							<ButtonBase
								onClick={() => setShowFolded((p) => !p)}
								aria-expanded={showFolded}
								data-testid={dataTestId ? `${dataTestId}-no-matches` : undefined}
								sx={{
									alignSelf: "flex-start",
									mt: 0.5,
									px: 0.5,
									py: 0.25,
									borderRadius: "6px",
									fontSize: FONT_SIZE.SECONDARY,
									fontFamily: "inherit",
									color: INK.MUTED,
									"@media (hover: hover) and (pointer: fine)": {
										"&:hover": { textDecoration: "underline" },
									},
								}}
							>
								{showFolded ? t("filter.section.hide_no_matches") : t("filter.section.no_matches", { num: foldedOptions.length })}
							</ButtonBase>
							{showFolded &&
								foldedOptions.map((option) => (
									<SystemRoundedCheckbox
										key={option.id}
										checked={false}
										onChange={(checked) => onToggle(option.id, checked)}
										label={option.label}
										disabled={option.disabled ?? true}
										fullWidth
										secondary={option.count}
										startAdornment={
											option.colorDot ? (
												<Box sx={{ width: COLOR_DOT_SIZE, height: COLOR_DOT_SIZE, borderRadius: "50%", flexShrink: 0, bgcolor: option.colorDot }} />
											) : undefined
										}
										data-testid={dataTestId ? `${dataTestId}-option-${toTestIdSlug(option.id)}` : undefined}
										sx={{ py: 0.375, borderRadius: "6px" }}
									/>
								))}
						</>
					)}
				</Box>
			)}
		</Box>
	);
};
